import React from 'react';
import FeatureCards from "../../components/FeatureCards/FeatureCards";
import CTA from "../../components/Constant/cta/CTA";
import TimelineSection from "../../components/TimelineSection/TimelineSection";
import WhySection from "../../components/WhySection/WhySection";
import LocationTimeline from "../../components/LocationTimeline/LocationTimeline";
import IntroSection from "../../components/IntroSection/IntroSection";
import FAQList from "../../components/Constant/FAQCard/FAQCard";
import Testimonials from "../../components/Constant/Testimonials/Testimonials";
import DetailsSection from "../../components/DetailsSection/DetailsSection";
import InfoBlocks from "../../components/InfoBlocks/InfoBlocks";
import ServiceCards from "../../components/ServiceCards/ServiceCards";

export default function NonDomTaxRegime() {

    return (
        <main className='main'>
            <IntroSection
                title="Non-Dom Tax Regime"
                subtitle="Pay Less Tax, Live More Freely"
                description="Many countries offer Non-Domiciled (Non-Dom) tax regimes and favorable residency programs designed for investors, entrepreneurs, retirees, and high-net-worth individuals. These frameworks provide flat tax options, exemptions on foreign-sourced income, and simplified reporting—making them ideal for globally mobile individuals seeking tax efficiency, asset protection, and long-term financial freedom."
                badges={[
                    "Flat Tax Options",
                    "Exemptions on Foreign Income",
                    "Global Residency Solutions"
                ]}
                buttonText="Request a Tax Consultation →"
                buttonLink="/quote"
                imageSrc={require('../../assets/images/non-dom-tax-regim/non-dom-tax-regime.png')}
                imageAlt="Non-Dom Tax Regime"
            />

            <section className='section'>
                <div className="container">
                    <WhySection
                        title="Key Non-Dom Regime Jurisdictions"
                        blocks={[
                            {
                                subtitle: "Italy – Non-Dom Regime",
                                items: [
                                    { text: "Flat tax of €100,000 per year on foreign income." },
                                    { text: "Extendable to family members (€25,000 each)." },
                                    { text: "Valid for 15 years." },
                                    { text: "Popular among high-net-worth individuals relocating to Italy." }
                                ]
                            },
                            {
                                subtitle: "Portugal – Non-Habitual Resident (NHR) Regime",
                                items: [
                                    { text: "Tax benefits for 10 years." },
                                    { text: "0% tax on most foreign-sourced income (pensions, dividends, royalties)." },
                                    { text: "20% flat rate on certain Portuguese income for high-value professions." },
                                    { text: "Very attractive for retirees and remote workers." }
                                ]
                            },
                            {
                                subtitle: "Malta – Non-Dom Residency Program",
                                items: [
                                    { text: "Foreign income not remitted to Malta is exempt from tax." },
                                    { text: "Remitted income taxed at 15%." },
                                    { text: "No inheritance or wealth taxes." },
                                    { text: "Strong EU base with Schengen mobility." }
                                ]
                            },
                            {
                                subtitle: "Cyprus – Non-Dom Tax Incentives",
                                items: [
                                    { text: "0% tax on dividends and interest income for 17 years." },
                                    { text: "No wealth, inheritance, or gift tax." },
                                    { text: "Popular for entrepreneurs and business relocations." },
                                    { text: "EU jurisdiction with extensive double tax treaties." }
                                ]
                            },
                            {
                                subtitle: "Greece – Non-Dom Flat Tax Regime",
                                items: [
                                    { text: "Flat tax of €100,000 per year on global income." },
                                    { text: "Valid for 15 years." },
                                    { text: "Exemptions for foreign inheritance & donations." },
                                    { text: "Popular among wealthy investors relocating to Greece." }
                                ]
                            },
                            {
                                subtitle: "United Kingdom – Non-Dom Resident Status",
                                items: [
                                    { text: "Option to be taxed only on UK-sourced income." },
                                    { text: "Foreign income taxed only if remitted to the UK." },
                                    { text: "Historically attractive for global wealth, although rules have tightened." }
                                ]
                            },
                            {
                                subtitle: "Panama – Territorial Tax System",
                                items: [
                                    { text: "Foreign income is not taxed." },
                                    { text: "Only Panama-sourced income is taxable." },
                                    { text: "Flexible residency visas (Friendly Nations, Pensionado, Investor Residency)." },
                                    { text: "Strategic location with a USD-based economy." }
                                ]
                            }
                        ]}
                    />

                    <WhySection
                        title="Benefits of Choosing a Non-Dom Tax Regime"
                        blocks={[
                            {
                                items: [
                                    { title: "Flat Tax Options", text: "Predictable annual payments regardless of income size." },
                                    { title: "Exemption from Foreign Income", text: "Keep global income untaxed or lightly taxed." },
                                    { title: "Banking & Investment Freedom", text: "Avoid double taxation and maintain global flexibility." },
                                    { title: "Family Security", text: "Extend tax benefits to qualifying family members." },
                                    { title: "Legal Certainty", text: "Compliant tax optimization with clear local frameworks." }
                                ]
                            }
                        ]}
                    />

                    <InfoBlocks
                        title="How Offshore Advisory Helps"
                        blocks={[
                            {
                                items: [
                                    { text: "Eligibility Check — We determine which regimes you qualify for." },
                                    { text: "Jurisdiction Comparison — Italy vs Portugal vs Malta vs Cyprus vs Greece vs Panama." },
                                    { text: "Application Filing — Residency setup, tax rulings, and compliance filings." },
                                    { text: "Ongoing Advisory — Renewals, structuring, and cross-border tax planning." }
                                ]
                            }
                        ]}
                    />


                    <FAQList data={faqData} title='Frequently Asked Questions (FAQ)'/>

                    <Testimonials
                        title="Client Testimonials"
                        testimonials={testimonialsData}
                    />

                    <CTA
                        title="Looking for the best Non-Dom or tax residency option?"
                        description="From Italy and Portugal to Panama and Malta, Offshore Advisory helps you secure the most favorable global tax regime."
                        buttonText="Book a Tax Consultation →"
                        buttonLink="/quote"
                    />

                </div>
            </section>
        </main>
    );
};

const faqData = [
    {
        question: "What is a Non-Dom regime?",
        answer: "It’s a special tax regime allowing residents to limit or reduce taxation on foreign income.",
    },
    {
        question: "Do I need to move permanently to benefit?",
        answer: "Most regimes require tax residency but not always permanent relocation (depends on jurisdiction).",
    },
    {
        question: "How long do these regimes last?",
        answer: "Ranges from 10 to 17 years (Portugal, Cyprus) to 15 years (Italy, Greece).",
    },
    {
        question: "What’s the difference between Non-Dom and Territorial Tax Systems?",
        answer:
            "Non-Dom: Tax residency with exemptions or flat tax on foreign income. Territorial: Only income earned in the country is taxed (e.g., Panama, UAE).",
    },
    {
        question: "Can family members be included?",
        answer:
            "Yes, Italy and Greece allow extensions; others allow dependents under residency visas.",
    },
];



const testimonialsData =
    {
        quote: "Offshore Advisory compared Portugal’s NHR with Italy’s Non-Dom for me. I chose Malta, and my tax planning is now secure.",
        author: "Sophia G.",
        role: "Investor"
    }
;
